<script setup lang="ts">
import { computed } from 'vue'
import Avatar from '@/components/shadcn/Avatar.vue'
import AvatarFallback from '@/components/shadcn/AvatarFallback.vue'
import AvatarImage from '@/components/shadcn/AvatarImage.vue'

type StyleType =
  | 'default'   // 기본 스타일
  | 'primary'   // 강조 스타일 (파랑)
  | 'success'   // 성공 스타일 (초록)
  | 'warning'   // 경고 스타일 (주황)
  | 'danger'    // 위험 스타일 (빨강)

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

type Shape = 'circle' | 'square' | 'rounded'

type Status = 'online' | 'offline' | 'away' | 'busy'

const props = withDefaults(
  defineProps<{
    src?: string
    alt?: string
    fallback?: string
    class?: string
    /** 스타일 프리셋 */
    styletype?: StyleType
    /** 아바타 크기 */
    size?: Size
    /** 아바타 모양 */
    shape?: Shape
    /** 상태 표시 */
    status?: Status
  }>(),
  {
    alt: '',
    fallback: '?',
    styletype: 'default',
    size: 'sm',
    shape: 'circle',
  },
)

/**
 * styletype -> class 매핑
 */
const STYLE_PRESETS: Record<StyleType, { class: string }> = {
  default: { class: '' },
  primary: { 
    class: 'bg-blue-500 text-white',
  },
  success: { 
    class: 'bg-green-500 text-white',
  },
  warning: { 
    class: 'bg-amber-500 text-white',
  },
  danger: { 
    class: 'bg-red-500 text-white',
  },
}

/**
 * size -> class 매핑
 */
const SIZE_PRESETS: Record<Size, { class: string }> = {
  xs: { 
    class: 'h-6 w-6 text-[10px]',
  },
  sm: { 
    class: 'h-8 w-8 text-xs',
  },
  md: { 
    class: 'h-10 w-10 text-sm',
  },
  lg: { 
    class: 'h-12 w-12 text-base',
  },
  xl: { 
    class: 'h-16 w-16 text-lg',
  },
}

/**
 * shape -> class 매핑
 */
const SHAPE_PRESETS: Record<Shape, { class: string }> = {
  circle: { 
    class: 'rounded-full',
  },
  square: { 
    class: 'rounded-none',
  },
  rounded: { 
    class: 'rounded-lg',
  },
}

/**
 * status -> class 매핑
 */
const STATUS_PRESETS: Record<Status, { class: string }> = {
  online: { 
    class: 'ring-2 ring-green-500 ring-offset-2',
  },
  offline: { 
    class: 'ring-2 ring-gray-400 ring-offset-2',
  },
  away: { 
    class: 'ring-2 ring-yellow-500 ring-offset-2',
  },
  busy: { 
    class: 'ring-2 ring-red-500 ring-offset-2',
  },
}

/** 최종 바인딩: 직접 넘긴 class가 있으면 styletype, size, shape, status 기본값과 병합 */
const mapped = computed(() => {
  const stylePreset = STYLE_PRESETS[props.styletype!]
  const sizePreset = SIZE_PRESETS[props.size!]
  const shapePreset = SHAPE_PRESETS[props.shape!]
  const statusPreset = props.status ? STATUS_PRESETS[props.status] : { class: '' }
  const finalClass = [stylePreset.class, sizePreset.class, shapePreset.class, statusPreset.class, props.class].filter(Boolean).join(' ')
  
  return {
    class: finalClass,
  }
})
</script>

<template>
  <Avatar v-bind="mapped">
    <AvatarImage v-if="props.src" :src="props.src" :alt="props.alt" />
    <AvatarFallback v-else>{{ props.fallback }}</AvatarFallback>
  </Avatar>
</template>
